// app/components/Plugins/Automations/Automations.tsx
'use client'

import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  NodeTypes,
  Connection,
  Edge,
  BackgroundVariant
} from '@xyflow/react'
import { ConnectorManager } from '../../Connectors/ConnectorManager'

// Import core engines
import { FlowEngine, ExecutionEngine, NodeFactory, DataProvider } from './core'

// Import technical indicators
import { RSI, MACD, SMA, EMA, BollingerBands } from 'technicalindicators'

import '@xyflow/react/dist/style.css'
import './nodes/nodes.css'
import styles from './Automations.module.css'


// Custom node components
import StartNode from './nodes/StartNode'
import IndicatorNode from './nodes/IndicatorNode'
import BuySellNode from './nodes/BuySellNode'
import StatusNode from './nodes/StatusNode'
import StopNode from './nodes/StopNode'

// Types
import { AutomationNode, NodeData, ExecutionLog, AutomationEdge, BackgroundVariant as AutomationBackgroundVariant } from './types'

/**
 * @uiComponent
 * @name Automations
 * @description The Automations component provides a visual flow-based automation builder for trading strategies.
 * It features a drag-and-drop interface with nodes for indicators, buy/sell actions, and flow control.
 * Users can create complex trading automations by connecting different node types in a visual workflow.
 * Includes real-time execution monitoring, logging, and integration with multiple trading connectors.
 * @location Automations plugin in the trading terminal
 * @prop none - No external props, manages internal state and flow execution
 */

/**
 * @uiWorkflow
 * @name Create Basic Trading Automation
 * @description How to build a simple automated trading strategy using visual nodes
 * @platform automations
 * @step 1 Navigate to the Automations plugin [component: Automations] [action: navigate]
 * @step 2 Configure the Start Node with your target tickers [component: StartNode] [action: edit] [notes: Enter comma-separated ticker symbols]
 * @step 3 Add an Indicator Node from the toolbar [component: Automations] [action: click] [notes: Click "Add Indicator" button]
 * @step 4 Configure the indicator settings (RSI, MACD, etc.) [component: IndicatorNode] [action: edit]
 * @step 5 Set the condition and threshold values [component: IndicatorNode] [action: configure]
 * @step 6 Add a Buy/Sell Node from the toolbar [component: Automations] [action: click]
 * @step 7 Configure the trading action and quantity [component: BuySellNode] [action: edit]
 * @step 8 Connect the nodes by dragging from output to input [component: Automations] [action: drag]
 * @step 9 Add a Stop Node to complete the flow [component: Automations] [action: click]
 * @step 10 Connect the final node to the Stop Node [component: Automations] [action: drag]
 * @step 11 Click "Start Automation" to begin execution [component: Automations] [action: click]
 * @step 12 Monitor execution logs in the bottom panel [component: Automations] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Configure Advanced Technical Indicators
 * @description How to set up complex indicator-based trading conditions
 * @platform automations
 * @step 1 Add an Indicator Node to your automation flow [component: Automations] [action: click]
 * @step 2 Click on the Indicator Node to open settings [component: IndicatorNode] [action: click]
 * @step 3 Select your desired indicator type [component: IndicatorNode] [action: select] [notes: RSI, MACD, SMA, EMA, Bollinger Bands]
 * @step 4 Choose the timeframe for analysis [component: IndicatorNode] [action: select] [notes: 1m, 5m, 15m, 1h, 1d]
 * @step 5 Set the condition logic [component: IndicatorNode] [action: select] [notes: crosses_above, crosses_below, greater_than, less_than]
 * @step 6 Enter the threshold value [component: IndicatorNode] [action: type] [notes: e.g., 70 for RSI overbought]
 * @step 7 Connect the indicator to subsequent action nodes [component: Automations] [action: drag]
 * @step 8 Test the configuration with historical data [component: Automations] [action: validate]
 */

/**
 * @uiWorkflow
 * @name Monitor and Debug Automation Execution
 * @description How to track automation performance and troubleshoot issues
 * @platform automations
 * @step 1 Start your automation flow [component: Automations] [action: click] [notes: Click "Start Automation" button]
 * @step 2 Watch the Status Node for execution state [component: StatusNode] [action: observe] [notes: IDLE → IN_PROGRESS → COMPLETED]
 * @step 3 Monitor real-time logs in the execution panel [component: Automations] [action: scroll]
 * @step 4 Check for error messages or warnings [component: Automations] [action: read]
 * @step 5 Observe node highlighting during execution [component: Automations] [action: watch] [notes: Active nodes are highlighted]
 * @step 6 Stop automation if needed [component: Automations] [action: click] [notes: Click "Stop Automation" button]
 * @step 7 Review execution history and performance [component: Automations] [action: analyze]
 * @step 8 Adjust node configurations based on results [component: Automations] [action: edit]
 */

/**
 * @uiComponent
 * @name Automations
 * @description The Automations component provides a visual flow-based automation builder for trading strategies.
 * It features a drag-and-drop interface with nodes for indicators, buy/sell actions, and flow control.
 * Users can create complex trading automations by connecting different node types in a visual workflow.
 * Includes real-time execution monitoring, logging, and integration with multiple trading connectors.
 * @location Automations plugin in the trading terminal
 * @prop none - No external props, manages internal state and flow execution
 */

/**
 * @uiWorkflow
 * @name Create Basic Trading Automation
 * @description How to build a simple automated trading strategy using visual nodes
 * @platform automations
 * @step 1 Navigate to the Automations plugin [component: Automations] [action: navigate]
 * @step 2 Configure the Start Node with your target tickers [component: StartNode] [action: edit] [notes: Enter comma-separated ticker symbols]
 * @step 3 Add an Indicator Node from the toolbar [component: Automations] [action: click] [notes: Click "Add Indicator" button]
 * @step 4 Configure the indicator settings (RSI, MACD, etc.) [component: IndicatorNode] [action: edit]
 * @step 5 Set the condition and threshold values [component: IndicatorNode] [action: configure]
 * @step 6 Add a Buy/Sell Node from the toolbar [component: Automations] [action: click]
 * @step 7 Configure the trading action and quantity [component: BuySellNode] [action: edit]
 * @step 8 Connect the nodes by dragging from output to input [component: Automations] [action: drag]
 * @step 9 Add a Stop Node to complete the flow [component: Automations] [action: click]
 * @step 10 Connect the final node to the Stop Node [component: Automations] [action: drag]
 * @step 11 Click "Start Automation" to begin execution [component: Automations] [action: click]
 * @step 12 Monitor execution logs in the bottom panel [component: Automations] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Configure Advanced Technical Indicators
 * @description How to set up complex indicator-based trading conditions
 * @platform automations
 * @step 1 Add an Indicator Node to your automation flow [component: Automations] [action: click]
 * @step 2 Click on the Indicator Node to open settings [component: IndicatorNode] [action: click]
 * @step 3 Select your desired indicator type [component: IndicatorNode] [action: select] [notes: RSI, MACD, SMA, EMA, Bollinger Bands]
 * @step 4 Choose the timeframe for analysis [component: IndicatorNode] [action: select] [notes: 1m, 5m, 15m, 1h, 1d]
 * @step 5 Set the condition logic [component: IndicatorNode] [action: select] [notes: crosses_above, crosses_below, greater_than, less_than]
 * @step 6 Enter the threshold value [component: IndicatorNode] [action: type] [notes: e.g., 70 for RSI overbought]
 * @step 7 Connect the indicator to subsequent action nodes [component: Automations] [action: drag]
 * @step 8 Test the configuration with historical data [component: Automations] [action: validate]
 */

/**
 * @uiWorkflow
 * @name Monitor and Debug Automation Execution
 * @description How to track automation performance and troubleshoot issues
 * @platform automations
 * @step 1 Start your automation flow [component: Automations] [action: click] [notes: Click "Start Automation" button]
 * @step 2 Watch the Status Node for execution state [component: StatusNode] [action: observe] [notes: IDLE → IN_PROGRESS → COMPLETED]
 * @step 3 Monitor real-time logs in the execution panel [component: Automations] [action: scroll]
 * @step 4 Check for error messages or warnings [component: Automations] [action: read]
 * @step 5 Observe node highlighting during execution [component: Automations] [action: watch] [notes: Active nodes are highlighted]
 * @step 6 Stop automation if needed [component: Automations] [action: click] [notes: Click "Stop Automation" button]
 * @step 7 Review execution history and performance [component: Automations] [action: analyze]
 * @step 8 Adjust node configurations based on results [component: Automations] [action: edit]
 */

/**
 * @uiWorkflow
 * @name Save and Load Automation Templates
 * @description How to manage and reuse automation configurations
 * @platform automations
 * @step 1 Create your automation flow with all nodes configured [component: Automations] [action: build]
 * @step 2 Test the automation to ensure it works correctly [component: Automations] [action: test]
 * @step 3 Use the save functionality to store the configuration [component: Automations] [action: save] [notes: Save as JSON template]
 * @step 4 Name your automation template descriptively [component: Automations] [action: type]
 * @step 5 Load existing templates when needed [component: Automations] [action: load]
 * @step 6 Modify loaded templates for different strategies [component: Automations] [action: edit]
 * @step 7 Share templates with other users [component: Automations] [action: export]
 */

// Node types registration
const nodeTypes: NodeTypes = {
  startNode: StartNode,
  indicatorNode: IndicatorNode,
  buySellNode: BuySellNode,
  statusNode: StatusNode,
  stopNode: StopNode
}

const proOptions = { hideAttribution: true, animated: true  };
const edgeOptions = {
  animated: true,
  // type: 'smoothstep',
  style: {
    stroke: '#88a1ac',
    strokeWidth: 2,
    strokeDasharray: '5, 5',
    targetMarker: {
      type: 'path',
      d: 'M 0 0 L 10 5 L 0 10 z'
    }
  },
}

export default function Automations() {
  // Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<AutomationNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<AutomationEdge>([])
  
  // Execution state
  const [isRunning, setIsRunning] = useState(false)
  const isRunningRef = useRef(isRunning)
  useEffect(() => { isRunningRef.current = isRunning }, [isRunning])
  const [logs, setLogs] = useState<ExecutionLog[]>([])
  const logsEndRef = useRef<HTMLDivElement>(null)
  
  // Initialize with default nodes
  useEffect(() => {
    const initialNodes: AutomationNode[] = [
      {
        id: 'start-1',
        type: 'startNode',
        position: { x: 250, y: 100 },
        data: { label: 'Start Node', tickers: '' }
      },
      {
        id: 'status-1',
        type: 'statusNode',
        position: { x: 700, y: 100 },
        data: { label: 'Status', status: 'IDLE' }
      }
    ]
    
    setNodes(initialNodes)
  }, [])
  
  // Auto-scroll logs to bottom (only within logs container)
  useEffect(() => {
    if (logsEndRef.current) {
      const logsContainer = logsEndRef.current.parentElement
      if (logsContainer) {
        logsContainer.scrollTop = logsContainer.scrollHeight
      }
    }
  }, [logs])
  
  // Handle connections between nodes
  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )
  
  // Add a new node to the flow
  const addNode = (type: string) => {
    const newNode: AutomationNode = {
      id: `${type}-${Date.now()}`,
      type: type as any,
      position: { x: 400, y: 200 },
      data: { 
        label: type === 'indicatorNode' ? 'Indicator' : 
               type === 'buySellNode' ? 'Buy/Sell' : 
               type === 'stopNode' ? 'Stop' : type,
        // Default data based on node type
        ...(type === 'indicatorNode' 
          ? { indicator: 'RSI', timeframe: '1d', condition: 'crosses_above', value: '70' }
          : type === 'buySellNode' 
          ? { 
              action: 'BUY', 
              quantity: '1',
              orderType: 'MARKET',
              connector: '',
              price: ''
            }
          : {})
      }
    }
    
    setNodes((nds) => [...nds, newNode])
    addLog(`Added new ${type.replace('Node', '')} node`)
  }
  
  // Add a log entry
  const addLog = (message: string) => {
    setLogs((prevLogs) => [
      ...prevLogs,
      { timestamp: new Date(), message }
    ])
  }
  
  // Start the automation flow
  const startAutomation = () => {
    setIsRunning(true)
    
    // Find start node
    const startNode = nodes.find(node => node.type === 'startNode')
    if (!startNode) {
      addLog('Error: Start node not found')
      setIsRunning(false)
      return
    }
    
    // Get tickers from start node
    const tickers = startNode.data.tickers || ''
    if (!tickers.trim()) {
      addLog('Error: No tickers specified in Start Node')
      setIsRunning(false)
      return
    }
    
    // Update start node to indicate it's running
    setNodes(nodes.map(node => {
      if (node.id === startNode.id) {
        return {
          ...node,
          data: {
            ...node.data,
            isRunning: true
          }
        }
      }
      return node
    }))
    
    // Update status nodes to IN_PROGRESS
    setNodes(nodes.map(node => {
      if (node.type === 'statusNode') {
        return {
          ...node,
          data: {
            ...node.data,
            status: 'IN_PROGRESS'
          }
        }
      }
      return node
    }))
    
    // Create a new FlowEngine instance
    const flowEngine = new FlowEngine()
    
    // Add all nodes to the flow engine
    nodes.forEach(node => {
      flowEngine.nodes.set(node.id, NodeFactory.createNode(node))
    })
    
    // Add all edges to the flow engine
    flowEngine.edges = edges
    
    // Validate the flow
    const validation = flowEngine.validate()
    if (!validation.valid) {
      addLog('Flow validation failed:')
      validation.errors.forEach(error => addLog(`Error: ${error}`))
      setIsRunning(false)
      
      // Update status nodes to ERROR
      setNodes(nodes.map(node => {
        if (node.type === 'statusNode') {
          return {
            ...node,
            data: {
              ...node.data,
              status: 'ERROR'
            }
          }
        }
        return node
      }))
      
      return
    }
    
    // Start processing tickers
    addLog(`Starting automation with tickers: ${tickers}`)
    processNextTicker(tickers, 0, startNode.id)
  }
  
  // Process a node and its connected nodes recursively
  const processNode = (nodeId: string, visitedNodes: Set<string>, onComplete: () => void, tickers: string, tickerIndex: number) => {
    // If we've already visited this node, avoid cycles
    if (visitedNodes.has(nodeId)) {
      onComplete()
      return
    }
    
    // Mark this node as visited
    visitedNodes.add(nodeId)
    
    // Find the node
    const node = nodes.find(n => n.id === nodeId)
    if (!node) {
      onComplete()
      return
    }
    
    // Clear processing flag from all nodes first, then set it on the current node
    setNodes(nds => nds.map(n => {
      if (n.id === nodeId) {
        return {
          ...n,
          data: { ...n.data, processing: true },
          className: 'processing' // Add a class for styling
        }
      }
      // Remove processing state from all other nodes
      return {
        ...n,
        data: { ...n.data, processing: false },
        className: n.className === 'processing' ? '' : n.className
      }
    }))
    
    // Find all outgoing connections from this node
    const nodeConnections = edges.filter(edge => edge.source === nodeId)
    const connectedNodeIds = nodeConnections.map(conn => conn.target)
    
    // Process the current node based on its type
    switch (node.type) {
      case 'startNode':
        // Start node just passes control to its connected nodes
        if (connectedNodeIds.length === 0) {
          addLog('No connections from start node')
          // Clear processing flag
          setNodes(nds => nds.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                data: { ...n.data, processing: false },
                className: ''
              }
            }
            return n
          }))
          onComplete()
          return
        }
        
        // Process all connected nodes
        processConnectedNodes(nodeId, connectedNodeIds, visitedNodes, () => {
          // Clear processing flag when all connected nodes are processed
          setNodes(nds => nds.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                data: { ...n.data, processing: false },
                className: ''
              }
            }
            return n
          }))
          onComplete()
        }, tickers, tickerIndex)
        break
        
      case 'indicatorNode':
        // Process indicator node using the actual implementation
        setTimeout(async () => {
          try {
            // Create a data provider instance
            const dataProvider = new DataProvider()
            
            // Get the ticker for this iteration
            const tickersArray = tickers.split(',').map(ticker => ticker.trim())
            const currentTicker = tickersArray[tickerIndex]
            
            // Get indicator configuration from node data
            const { indicator, timeframe, condition, value } = node.data
            
            // Create execution context for the indicator
            const context = {
              ticker: currentTicker,
              flowId: `flow-${Date.now()}`,
              nodeId: nodeId,
              data: {},
              dataProvider,
              addLog: (message: string) => addLog(`${currentTicker}: ${message}`)
            }
            
            // Get market data
            const marketData = await dataProvider.getMarketData(currentTicker, timeframe)
            
            // Calculate indicator value based on type
            let indicatorValue = null
            let conditionMet = false
            const thresholdValue = parseFloat(value as string)
            
            switch (indicator) {
              case 'Price': {
                const priceType = node.data.priceType || 'close'
                
                // Use type assertion to treat the price data as a number array
                const priceData = marketData[priceType] as number[]
                if (priceData && priceData.length > 0) {
                  // Use the latest price as the indicator value
                  indicatorValue = priceData[priceData.length - 1]
                  
                  // Log the current price value
                  addLog(`${currentTicker}: Current ${priceType} price: ${indicatorValue !== null ? indicatorValue.toFixed(2) : 'N/A'}`)
                  addLog(`${currentTicker}: Threshold value: ${thresholdValue}`)
                  
                  // Get previous price for crossing conditions
                  const prevPrice = priceData.length > 1 ? 
                    priceData[priceData.length - 2] : null
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = prevPrice !== null && prevPrice <= thresholdValue && indicatorValue > thresholdValue
                      break
                    case 'crosses_below':
                      conditionMet = prevPrice !== null && prevPrice >= thresholdValue && indicatorValue < thresholdValue
                      break
                    case 'is_above':
                      conditionMet = indicatorValue > thresholdValue
                      break
                    case 'is_below':
                      conditionMet = indicatorValue < thresholdValue
                      break
                    case 'is_increasing':
                      conditionMet = prevPrice !== null && indicatorValue > prevPrice
                      break
                    case 'is_decreasing':
                      conditionMet = prevPrice !== null && indicatorValue < prevPrice
                      break
                    default:
                      conditionMet = false
                  }
                  
                  // Log the condition result
                  addLog(`${currentTicker}: Condition '${condition.replace('_', ' ')}' is ${conditionMet ? 'MET' : 'NOT MET'}`)
                } else {
                  addLog(`${currentTicker}: No ${priceType} price data available`)
                }
                break
              }
              
              case 'RSI': {
                const period = node.data.rsiPeriod || 14
                const rsiValues = RSI.calculate({
                  values: marketData.close,
                  period: period
                })
                
                if (rsiValues.length > 0) {
                  indicatorValue = rsiValues[rsiValues.length - 1]
                  
                  // Log the actual RSI value
                  addLog(`${currentTicker}: RSI(${period}) value: ${indicatorValue.toFixed(2)}`)
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = indicatorValue > thresholdValue
                      break
                    case 'crosses_below':
                      conditionMet = indicatorValue < thresholdValue
                      break
                    case 'is_above':
                      conditionMet = indicatorValue > thresholdValue
                      break
                    case 'is_below':
                      conditionMet = indicatorValue < thresholdValue
                      break
                    case 'is_increasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = indicatorValue > 50
                      break
                    case 'is_decreasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = indicatorValue < 50
                      break
                    default:
                      conditionMet = false
                  }
                } else {
                  addLog(`${currentTicker}: Failed to calculate RSI values`)
                }
                break
              }
              
              case 'MACD': {
                const fastPeriod = node.data.macdFastPeriod || 12
                const slowPeriod = node.data.macdSlowPeriod || 26
                const signalPeriod = node.data.macdSignalPeriod || 9
                
                const macdResults = MACD.calculate({
                  values: marketData.close,
                  fastPeriod,
                  slowPeriod,
                  signalPeriod,
                  SimpleMAOscillator: false,
                  SimpleMASignal: false
                })
                
                if (macdResults.length > 0) {
                  const latestMACD = macdResults[macdResults.length - 1]
                  indicatorValue = latestMACD.MACD ?? 0
                  const signalValue = latestMACD.signal ?? 0
                  const histogramValue = latestMACD.histogram ?? 0
                  
                  // Log MACD values
                  addLog(`${currentTicker}: MACD(${fastPeriod},${slowPeriod},${signalPeriod}) values:`)
                  addLog(`- MACD Line: ${indicatorValue.toFixed(2)}`)
                  addLog(`- Signal Line: ${signalValue.toFixed(2)}`)
                  addLog(`- Histogram: ${histogramValue.toFixed(2)}`)
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = (indicatorValue ?? 0) > (signalValue ?? 0)
                      break
                    case 'crosses_below':
                      conditionMet = (indicatorValue ?? 0) < (signalValue ?? 0)
                      break
                    case 'is_above':
                      conditionMet = (indicatorValue ?? 0) > thresholdValue
                      break
                    case 'is_below':
                      conditionMet = (indicatorValue ?? 0) < thresholdValue
                      break
                    case 'is_increasing':
                      conditionMet = (histogramValue ?? 0) > 0
                      break
                    case 'is_decreasing':
                      conditionMet = (histogramValue ?? 0) < 0
                      break
                    default:
                      conditionMet = false
                  }
                } else {
                  addLog(`${currentTicker}: Failed to calculate MACD values`)
                }
                break
              }
              
              case 'SMA': {
                const period = node.data.smaPeriod || 20
                const smaValues = SMA.calculate({
                  values: marketData.close,
                  period: period
                })
                
                if (smaValues.length > 0) {
                  indicatorValue = smaValues[smaValues.length - 1]
                  const latestPrice = marketData.close[marketData.close.length - 1]
                  
                  // Log SMA value
                  addLog(`${currentTicker}: SMA(${period}) value: ${indicatorValue.toFixed(2)}`)
                  addLog(`${currentTicker}: Latest price: ${latestPrice.toFixed(2)}`)
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = latestPrice > indicatorValue
                      break
                    case 'crosses_below':
                      conditionMet = latestPrice < indicatorValue
                      break
                    case 'is_above':
                      conditionMet = indicatorValue > thresholdValue
                      break
                    case 'is_below':
                      conditionMet = indicatorValue < thresholdValue
                      break
                    case 'is_increasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = smaValues.length > 1 && indicatorValue > smaValues[smaValues.length - 2]
                      break
                    case 'is_decreasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = smaValues.length > 1 && indicatorValue < smaValues[smaValues.length - 2]
                      break
                    default:
                      conditionMet = false
                  }
                } else {
                  addLog(`${currentTicker}: Failed to calculate SMA values`)
                }
                break
              }
              
              case 'EMA': {
                const period = node.data.emaPeriod || 20
                const emaValues = EMA.calculate({
                  values: marketData.close,
                  period: period
                })
                
                if (emaValues.length > 0) {
                  indicatorValue = emaValues[emaValues.length - 1]
                  const latestPrice = marketData.close[marketData.close.length - 1]
                  
                  // Log EMA value
                  addLog(`${currentTicker}: EMA(${period}) value: ${indicatorValue.toFixed(2)}`)
                  addLog(`${currentTicker}: Latest price: ${latestPrice.toFixed(2)}`)
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = latestPrice > indicatorValue
                      break
                    case 'crosses_below':
                      conditionMet = latestPrice < indicatorValue
                      break
                    case 'is_above':
                      conditionMet = indicatorValue > thresholdValue
                      break
                    case 'is_below':
                      conditionMet = indicatorValue < thresholdValue
                      break
                    case 'is_increasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = emaValues.length > 1 && indicatorValue > emaValues[emaValues.length - 2]
                      break
                    case 'is_decreasing':
                      // Simplified check - would need previous values for proper implementation
                      conditionMet = emaValues.length > 1 && indicatorValue < emaValues[emaValues.length - 2]
                      break
                    default:
                      conditionMet = false
                  }
                } else {
                  addLog(`${currentTicker}: Failed to calculate EMA values`)
                }
                break
              }
              
              case 'Bollinger Bands': {
                const period = node.data.bbPeriod || 20
                const stdDev = node.data.bbStdDev || 2
                
                const bbResults = BollingerBands.calculate({
                  values: marketData.close,
                  period: period,
                  stdDev: stdDev
                })
                
                if (bbResults.length > 0) {
                  const latestBB = bbResults[bbResults.length - 1]
                  const latestPrice = marketData.close[marketData.close.length - 1]
                  const selectedBand = value || 'upper'
                  
                  // Get the appropriate band value based on user selection
                  switch (selectedBand) {
                    case 'upper':
                      indicatorValue = latestBB.upper
                      break
                    case 'middle':
                      indicatorValue = latestBB.middle
                      break
                    case 'lower':
                      indicatorValue = latestBB.lower
                      break
                    default:
                      indicatorValue = latestBB.upper
                  }
                  
                  // Log Bollinger Bands values
                  addLog(`${currentTicker}: Bollinger Bands(${period},${stdDev}) values:`)
                  addLog(`- Upper Band: ${latestBB.upper.toFixed(2)}`)
                  addLog(`- Middle Band: ${latestBB.middle.toFixed(2)}`)
                  addLog(`- Lower Band: ${latestBB.lower.toFixed(2)}`)
                  addLog(`- Latest price: ${latestPrice.toFixed(2)}`)
                  
                  // Evaluate condition
                  switch (condition) {
                    case 'crosses_above':
                      conditionMet = latestPrice > indicatorValue
                      break
                    case 'crosses_below':
                      conditionMet = latestPrice < indicatorValue
                      break
                    case 'is_above':
                      conditionMet = latestPrice > indicatorValue
                      break
                    case 'is_below':
                      conditionMet = latestPrice < indicatorValue
                      break
                    default:
                      conditionMet = false
                  }
                } else {
                  addLog(`${currentTicker}: Failed to calculate Bollinger Bands values`)
                }
                break
              }
              
              default:
                addLog(`${currentTicker}: Unknown indicator type ${indicator}`)
                break
            }
            
            if (conditionMet) {
              addLog(`${currentTicker}: ${indicator} condition met`)
              
              // If indicator condition is met, continue to connected nodes
              processConnectedNodes(nodeId, connectedNodeIds, visitedNodes, () => {
                // Clear processing flag
                setNodes(nds => nds.map(n => {
                  if (n.id === nodeId) {
                    return {
                      ...n,
                      data: { ...n.data, processing: false },
                      className: ''
                    }
                  }
                  return n
                }))
                onComplete()
              }, tickers, tickerIndex)
            } else {
              addLog(`${currentTicker}: ${indicator} condition not met`)
              // Clear processing flag
              setNodes(nds => nds.map(n => {
                if (n.id === nodeId) {
                  return {
                    ...n,
                    data: { ...n.data, processing: false },
                    className: ''
                  }
                }
                return n
              }))
              onComplete()
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)
            addLog(`Error processing indicator node: ${errorMessage}`)
            
            // Clear processing flag
            setNodes(nds => nds.map(n => {
              if (n.id === nodeId) {
                return {
                  ...n,
                  data: { ...n.data, processing: false },
                  className: ''
                }
              }
              return n
            }))
            onComplete()
          }
        }, 1000)
        break
        
      case 'buySellNode':
        // Process buy/sell node with a delay to simulate order execution
        setTimeout(async () => {
          const connectorId = node.data.connector || 'nintrade' // Default to nintrade if not specified
          const orderType = node.data.orderType || 'MARKET'
          const price = node.data.price || ''
          const action = node.data.action || 'BUY'
          const quantity = node.data.quantity || '1'
          
          // Get broker name for display
          const connectorManager = ConnectorManager.getInstance()
          const brokers = connectorManager.getBrokers()
          const broker = brokers.find(b => b.id === connectorId)
          const brokerName = broker ? broker.name : 'Default'
          
          try {
            // Get the connector instance
            const connector = connectorManager.getConnector(connectorId)
            
            // Log connector status for debugging
            addLog(`Using broker: ${brokerName} (ID: ${connectorId})`)
            
            // Extract exchange information from symbol suffix and format the symbol
            let exchange = ''
            const tickersArray = tickers.split(',').map(ticker => ticker.trim())
            const currentTicker = tickersArray[tickerIndex]
            const formattedSymbol = currentTicker.replace(/\.(NS|BS)$/i, (match, group) => {
              exchange = group.toUpperCase() === 'NS' ? 'NSE' : 'BSE'
              return ''
            })
            
            // Log the formatted symbol for debugging
            addLog(`Formatted symbol: ${formattedSymbol}${exchange ? ` (Exchange: ${exchange})` : ''}`)
            
            if (!connector) {
              addLog(`${currentTicker}: Failed to execute ${action} order - Connector '${connectorId}' not found`)
              return
            }
            
            if (!connector.isConnectedToBroker) {
              addLog(`${currentTicker}: Failed to execute ${action} order - Connector '${connectorId}' does not implement isConnectedToBroker method`)
              return
            }
            
            if (!connector.isConnectedToBroker()) {
              addLog(`${currentTicker}: Failed to execute ${action} order - ${brokerName} connector not connected to broker`)
              return
            }
            
            if (!connector.placeOrder) {
              addLog(`${currentTicker}: Failed to execute ${action} order - Connector '${connectorId}' does not implement placeOrder method`)
              return
            }
            
            // Prepare the order object
            const order = {
              symbol: formattedSymbol,
              quantity: parseFloat(quantity),
              exchange: exchange || undefined, // Include exchange info if available
              price: orderType === 'LIMIT' ? parseFloat(price) : undefined,
              type: orderType,
              side: action
            }
            
            // Log order details before execution
            addLog(`Placing ${action} ${orderType} order for ${quantity} ${formattedSymbol} via ${brokerName}`)
            
            // Execute the order
            const result = await connector.placeOrder(order)
            
            // Log success with order details
            addLog(`${currentTicker}: Successfully executed ${action} ${orderType} order for ${quantity} shares via ${brokerName}${orderType === 'LIMIT' && price ? ` at $${price}` : ''}`)
            addLog(`Order ID: ${result.orderId || 'N/A'}, Status: ${result.status || 'SUBMITTED'}`)
          } catch (error) {
            // Log detailed error
            const errorMessage = error instanceof Error ? error.message : String(error)
            const tickersArray = tickers.split(',').map(ticker => ticker.trim())
            const currentTicker = tickersArray[tickerIndex]
            
            addLog(`${currentTicker}: Error executing ${action} order: ${errorMessage}`)
            console.error(`Order execution error for ${currentTicker}:`, error)
          }
          
          // Continue to connected nodes regardless of order success
          processConnectedNodes(nodeId, connectedNodeIds, visitedNodes, () => {
            // Clear processing flag
            setNodes(nds => nds.map(n => {
              if (n.id === nodeId) {
                return {
                  ...n,
                  data: { ...n.data, processing: false },
                  className: ''
                }
              }
              return n
            }))
            onComplete()
          }, tickers, tickerIndex)
        }, 1000)
        break
        
      case 'statusNode':
        // Status node is a terminal node, just complete
        // Clear processing flag
        setNodes(nds => nds.map(n => {
          if (n.id === nodeId) {
            return {
              ...n,
              data: { ...n.data, processing: false },
              className: ''
            }
          }
          return n
        }))
        onComplete()
        break
        
        case 'stopNode':
          // Stop node halts execution
          setTimeout(() => {
            addLog(`Stop node reached - halting execution`)
            
            // Update status nodes to COMPLETED
            setNodes(nds => nds.map(n => {
              if (n.type === 'statusNode') {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    status: 'COMPLETED'
                  }
                }
              } else if (n.id === nodeId) {
                // Clear processing flag for the stop node
                return {
                  ...n,
                  data: { ...n.data, processing: false },
                  className: ''
                }
              }
              return n
            }))
            
            // Stop execution
            setIsRunning(false)
            
            // Complete this branch
            onComplete()
          }, 500) // Small delay for visual effect
          break
        
      default:
        // For any other node types that might be added in the future
        addLog(`Processing node of type: ${node.type}`)
        processConnectedNodes(nodeId, connectedNodeIds, visitedNodes, () => {
          // Clear processing flag
          setNodes(nds => nds.map(n => {
            if (n.id === nodeId) {
              return {
                ...n,
                data: { ...n.data, processing: false },
                className: ''
              }
            }
            return n
          }))
          onComplete()
        }, tickers, tickerIndex)
        break
    }
  }
  
  // Helper function to process multiple connected nodes sequentially
  const processConnectedNodes = (
    sourceNodeId: string,
    connectedNodeIds: string[],
    visitedNodes: Set<string>,
    onComplete: () => void,
    tickers: string,
    tickerIndex: number
  ) => {
    if (connectedNodeIds.length === 0) {
      onComplete()
      return
    }
    
    // Process nodes sequentially to ensure proper highlighting
    const processSequentially = (index: number) => {
      if (index >= connectedNodeIds.length) {
        // All nodes processed
        onComplete()
        return
      }
      
      // Process current node, then move to the next one when complete
      processNode(connectedNodeIds[index], new Set(visitedNodes), () => {
        processSequentially(index + 1)
      }, tickers, tickerIndex)
    }
    
    // Start processing with the first node
    processSequentially(0)
  }
  
  // Function to process tickers sequentially
  const processNextTicker = (tickers: string, tickerIndex: number, startNodeId: string) => {
    // Split tickers string into array and trim whitespace from each ticker
    const tickersArray = tickers.split(',').map(ticker => ticker.trim())
    
    // If we've processed all tickers, we're done with this execution cycle
    if (tickerIndex >= tickersArray.length) {
      addLog('All tickers processed')
      
      // Update status nodes to COMPLETED
      setNodes(nodes.map(node => {
        if (node.type === 'statusNode') {
          return {
            ...node,
            data: {
              ...node.data,
              status: 'COMPLETED'
            }
          }
        }
        return node
      }))
      
      // Find the start node to check for recurring execution
      const startNode = nodes.find(node => node.id === startNodeId)
      if (startNode) {
        const frequency = startNode.data.frequency || 'once'
        
        // If frequency is not 'once', set up recurring execution
        if (frequency !== 'once') {
          // Calculate seconds for the next run
          let seconds = 0
          switch (frequency) {
            case '1m': seconds = 60; break
            case '5m': seconds = 5 * 60; break
            case '15m': seconds = 15 * 60; break
            case '30m': seconds = 30 * 60; break
            case '1hr': seconds = 60 * 60; break
            case 'custom': seconds = startNode.data.customSeconds || 60; break
          }
          
          // Update the start node with countdown
          setNodes(prevNodes => prevNodes.map(node => {
            if (node.id === startNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  countdown: seconds,
                  isRunning: true
                }
              }
            }
            return node
          }))
          
          addLog(`Flow will execute again in ${seconds} seconds`)
          
          // Set up timer for recurring execution
          const timerId = setTimeout(() => {
            // Only restart if we're still in running state
            if (isRunningRef.current) {
              addLog('Restarting flow execution based on frequency setting')
              processNextTicker(tickers, 0, startNodeId)
            }
          }, seconds * 1000)
          
          // Set up countdown interval
          const countdownInterval = setInterval(() => {
            setNodes(prevNodes => prevNodes.map(node => {
              if (node.id === startNodeId) {
                const currentCountdown = node.data.countdown || 0
                if (currentCountdown <= 0) {
                  // Clear the interval when countdown reaches 0
                  clearInterval(countdownInterval)
                  return node
                }
                // Update the countdown in the node data
                return {
                  ...node,
                  data: {
                    ...node.data,
                    countdown: currentCountdown - 1,
                    isRunning: true // Ensure isRunning remains true during countdown
                  }
                }
              }
              return node
            }))
          }, 1000)
          
          // Store the timer ID and countdown interval ID in the node data for potential cancellation
          setNodes(prevNodes => prevNodes.map(node => {
            if (node.id === startNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  timerId: timerId,
                  countdownIntervalId: countdownInterval
                }
              }
            }
            return node
          }))
          
          // Keep the running state true for recurring execution
          return
        }
      }
      
      // If we reach here, it's not a recurring execution or it's 'once' frequency
      setIsRunning(false)
      return
    }
    
    // Log which ticker we're processing
    addLog(`Processing ticker: ${tickersArray[tickerIndex]}`)
    
    // Find start node
    const startNode = nodes.find(node => node.id === startNodeId)
    if (!startNode) {
      addLog('Error: Start node not found')
      setIsRunning(false)
      return
    }
    
    // Process the flow starting from the start node
    processNode(startNode.id, new Set(), () => {
      // When all nodes in the flow are processed, move to the next ticker
      processNextTicker(tickers, tickerIndex + 1, startNodeId)
    }, tickers, tickerIndex)
  }
  
  
  // Stop the automation flow
  const stopAutomation = () => {
    setIsRunning(false)
    addLog('Automation stopped by user')
    
    // Update status nodes to IDLE
    setNodes(nodes.map(node => {
      if (node.type === 'statusNode') {
        return {
          ...node,
          data: {
            ...node.data,
            status: 'IDLE'
          }
        }
      }
      return node
    }))
    
    // Reset all start nodes - stop countdown, clear timers, and reset running state
    setNodes(nodes.map(node => {
      if (node.type === 'startNode') {
        // Clear any running timers
        if (node.data.timerId) {
          clearTimeout(node.data.timerId)
        }
        
        // Clear any countdown intervals
        if (node.data.countdownIntervalId) {
          clearInterval(node.data.countdownIntervalId)
        }
        
        return {
          ...node,
          data: {
            ...node.data,
            isRunning: false,
            countdown: 0,
            timerId: null,
            countdownIntervalId: null
          }
        }
      }
      return node
    }))
  }
  
  return (
    <div className={styles.container}>
      {/* <h2 className={styles.header}>Automations</h2> */}
      
      <div className={styles.flowContainer}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          proOptions={proOptions}
          defaultEdgeOptions={edgeOptions}
          nodesDraggable={!isRunning}
          nodesConnectable={!isRunning}
          elementsSelectable={!isRunning}
          nodeOrigin={[0.5, 0.5]}
        >
          <Controls />
          <MiniMap />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          
          <Panel position="top-right" className={styles.panel}>
            <button 
              onClick={() => addNode('indicatorNode')} 
              className={styles.button}
              disabled={isRunning}
            >
              Add Indicator
            </button>
            <button 
              onClick={() => addNode('buySellNode')} 
              className={styles.button}
              disabled={isRunning}
            >
              Add Buy/Sell
            </button>
            <button 
              onClick={() => addNode('stopNode')} 
              className={styles.button}
              disabled={isRunning}
            >
              Add Stop
            </button>
            {!isRunning ? (
              <button 
                onClick={startAutomation} 
                className={`${styles.button} ${styles.startButton}`}
              >
                Start
              </button>
            ) : (
              <button 
                onClick={stopAutomation} 
                className={`${styles.button} ${styles.stopButton}`}
              >
                Stop
              </button>
            )}
          </Panel>
        </ReactFlow>
      </div>
      
      <div className={styles.logsContainer}>
        <h3>Execution Logs</h3>
        <div className={styles.logs}>
          {logs.map((log, index) => (
            <div key={index} className={styles.logEntry}>
              <span className={styles.timestamp}>
                {log.timestamp.toLocaleTimeString()}
              </span>
              <span className={styles.message}>{log.message}</span>
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  )
}